/**
 * DailyWorksheet — the student's worksheet for a given school day, built from that
 * day's lesson. Renders as an actual worksheet (paper card): Part A multiple choice,
 * Part B fill-in-the-blank, Part C problem solving. Auto-grades objective items and
 * AI-grades open responses on submit. Includes a "Download PDF" printable version.
 */
import { RimClockDiagram } from './RimClockDiagram';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FileText, Download, Loader2, CheckCircle2, XCircle, Sparkles, RefreshCw, BookOpen, Cloud, CloudOff } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const RIM_CLOCK_PHOTO = 'https://static.prod-images.emergentagent.com/jobs/487214b0-3d92-439f-b796-348c9da9d362/images/3322f971e648d7e7cd7455e9268a2028c9db220cbb9df120b6217d0ebfe8c4bd.jpeg';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const LETTERS = ['A', 'B', 'C', 'D', 'E'];
const DRAFT_KEY = (date) => `saa_worksheet_draft_${date || 'today'}`;
const AUTOSAVE_MS = 2500; // server draft flush after 2.5s of typing pause

const DailyWorksheet = () => {
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | offline
  const [dateOverride, setDateOverride] = useState(null); // null = today, else YYYY-MM-DD
  const saveTimer = useRef(null);
  const answersRef = useRef({});
  answersRef.current = answers;

  // Build "make-up" date options: today back through Sept 8 (school-year start),
  // capped at 30 school days for UI sanity. Weekends filtered out.
  const dateOptions = React.useMemo(() => {
    const opts = [];
    const start = new Date('2026-09-08T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let cursor = new Date(today);
    let safety = 0;
    while (cursor >= start && safety < 45) {
      const day = cursor.getDay();
      if (day !== 0 && day !== 6) {
        const iso = cursor.toISOString().slice(0, 10);
        const isToday = iso === today.toISOString().slice(0, 10);
        const label = isToday
          ? 'Today'
          : cursor.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
        opts.push({ value: iso, label, isToday });
      }
      cursor.setDate(cursor.getDate() - 1);
      safety += 1;
    }
    return opts;
  }, []);

  // Most recent Friday option from dateOptions (weekend fallback CTA).
  const mostRecentFriday = React.useMemo(() => {
    return dateOptions.find((o) => {
      const dow = new Date(o.value + 'T00:00:00').getDay();
      return dow === 5;
    });
  }, [dateOptions]);

  const load = useCallback(async () => {
    setLoading(true);
    setResult(null);
    setAnswers({});
    try {
      const url = dateOverride
        ? `/school/daily-worksheet?date=${dateOverride}`
        : '/school/daily-worksheet';
      const r = await api.get(url, { headers: authHeaders() });
      const wsData = r.data.worksheet;
      setWs(wsData);
      if (r.data.submission) {
        setResult(r.data.submission);
        setAnswers(r.data.submission.answers || {});
      } else {
        // Restore answers priority: server draft → localStorage → empty.
        // Server draft wins because it syncs across devices; localStorage is the fallback for offline.
        const local = (() => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY(wsData?.date)) || 'null'); } catch { return null; } })();
        const serverAnswers = r.data.draft?.answers || null;
        if (serverAnswers && Object.keys(serverAnswers).length) {
          setAnswers(serverAnswers);
          setSaveStatus('saved');
        } else if (local && Object.keys(local).length) {
          setAnswers(local);
          setSaveStatus('saved');
        }
      }
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [dateOverride]);

  useEffect(() => { load(); }, [load]);

  // Autosave — writes to localStorage immediately, then debounces a server draft flush.
  const scheduleServerSave = useCallback(() => {
    if (!ws?.date || result) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveStatus('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        await api.post('/school/daily-worksheet/draft', { date: ws.date, answers: answersRef.current }, { headers: authHeaders() });
        setSaveStatus('saved');
      } catch {
        setSaveStatus('offline'); // still safe — localStorage has it
      }
    }, AUTOSAVE_MS);
  }, [ws, result]);

  const setAns = (id, val) => {
    setAnswers((a) => {
      const next = { ...a, [id]: val };
      // Instant local persistence — survives crashes, refreshes, phone dying
      try { localStorage.setItem(DRAFT_KEY(ws?.date), JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
    scheduleServerSave();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Flush any pending draft first so nothing is lost mid-submit.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      const r = await api.post('/school/daily-worksheet/submit', { date: ws.date, answers }, { headers: authHeaders() });
      setResult(r.data.submission);
      try { localStorage.removeItem(DRAFT_KEY(ws?.date)); } catch { /* noop */ }
      setSaveStatus('idle');
      toast.success('Worksheet graded');
    } catch (e) { toast.error('Could not submit — your answers are saved. Try again.'); }
    finally { setSubmitting(false); }
  };

  const retry = () => {
    setResult(null); setAnswers({}); setSaveStatus('idle');
    try { localStorage.removeItem(DRAFT_KEY(ws?.date)); } catch { /* noop */ }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/school/daily-worksheet/pdf?date=${ws.date}`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `PRSVR_Worksheet_${ws.date}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download PDF'); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="mb-6 border-2 border-zinc-800 px-5 py-4 flex items-center gap-3" data-testid="worksheet-loading">
        <Loader2 className="w-4 h-4 animate-spin text-orange-500" /><span className="text-xs text-zinc-500">Loading today's worksheet…</span>
      </div>
    );
  }
  if (!ws) return null;

  const qs = ws.questions || [];
  const resById = {};
  (result?.results || []).forEach((r) => { resById[r.id] = r; });
  const mc = qs.filter((q) => q.type === 'mc');
  const fb = qs.filter((q) => q.type === 'fill_blank');
  const pr = qs.filter((q) => q.type === 'problem');
  const num = {}; qs.forEach((q, i) => { num[q.id] = i + 1; });

  const QResult = ({ q }) => {
    const r = resById[q.id]; if (!r) return null;
    const ok = r.correct;
    return (
      <div className={`mt-2 text-[12px] px-3 py-2 border-l-4 ${ok ? 'border-emerald-500 bg-emerald-50' : 'border-red-400 bg-red-50'}`} data-testid={`ws-result-${q.id}`}>
        <div className={`font-bold flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-red-600'}`}>
          {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {q.type === 'problem' ? `Score: ${r.score ?? 0}/100` : (ok ? 'Correct' : 'Not quite')}
        </div>
        {r.feedback && <p className="text-zinc-700 mt-1 leading-snug">{r.feedback}</p>}
        {q.type !== 'problem' && !ok && r.answer && <p className="text-zinc-700 mt-1">Answer: <span className="font-bold">{r.answer}</span></p>}
        {r.accepted_synonym && <p className="text-zinc-700 mt-1" data-testid={`ws-synonym-${q.id}`}>Accepted as a synonym · the key word was <span className="font-bold">{r.answer}</span></p>}
        {r.explanation && <p className="text-zinc-500 mt-1 italic">{r.explanation}</p>}
      </div>
    );
  };

  return (
    <div className="mb-6 border-2 border-orange-500/40" data-testid="daily-worksheet">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-5 py-3 bg-orange-500/[0.05]" data-testid="worksheet-toggle">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-orange-500" />
          <div className="text-left">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 font-black">
              {dateOverride ? 'Make-Up Work' : ws?.is_weekend ? 'Weekend' : "Today's Worksheet"}
            </div>
            <div className="text-sm font-bold text-white">{ws.title}</div>
          </div>
        </div>
        {result && <span className="text-xs font-black text-emerald-400" data-testid="ws-objective-score">Objective: {result.objective_score}%</span>}
        {!result && saveStatus !== 'idle' && (
          <span className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${saveStatus === 'saving' ? 'text-amber-400' : saveStatus === 'offline' ? 'text-red-400' : 'text-emerald-400'}`} data-testid="worksheet-save-status">
            {saveStatus === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" /> Saving</>}
            {saveStatus === 'saved' && <><Cloud className="w-3 h-3" /> Saved</>}
            {saveStatus === 'offline' && <><CloudOff className="w-3 h-3" /> Saved · offline</>}
          </span>
        )}
      </button>

      {/* Make-up date picker — lets a student pull up any past school day
          (Sept 8+, weekdays only) to complete missed work. */}
      {open && dateOptions.length > 1 && (
        <div className="flex items-center gap-2 px-5 py-2 bg-black/40 border-b-2 border-orange-500/20" data-testid="worksheet-date-picker">
          <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500">Viewing:</span>
          <select
            value={dateOverride || dateOptions[0].value}
            onChange={(e) => {
              const v = e.target.value;
              setDateOverride(dateOptions[0].value === v ? null : v);
            }}
            className="bg-black border border-orange-500/40 text-white text-xs font-bold px-2 py-1 focus:outline-none focus:border-orange-500"
            data-testid="worksheet-date-select"
          >
            {dateOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}{o.isToday ? '' : ` · ${o.value}`}</option>
            ))}
          </select>
          {dateOverride && (
            <button
              onClick={() => setDateOverride(null)}
              className="text-[10px] font-black uppercase tracking-widest text-orange-400 hover:text-orange-300 underline"
              data-testid="worksheet-back-to-today"
            >
              ← Back to Today
            </button>
          )}
        </div>
      )}

      {open && ws?.is_weekend && !dateOverride && (
        <div className="bg-black/50 border-t-2 border-orange-500/40 px-5 py-6 text-center" data-testid="worksheet-weekend-callout">
          <div className="text-[10px] font-black tracking-[0.3em] uppercase text-orange-400 mb-2">
            No school today · {ws.title?.split('—')[1]?.trim() || 'Weekend'}
          </div>
          <div className="text-white text-lg font-black mb-1">Missing Friday's assignment?</div>
          <p className="text-xs text-zinc-400 mb-4 max-w-md mx-auto">
            The daily worksheet only runs Monday–Friday. Tap below to pull up the most recent school-day worksheet — you can complete it now.
          </p>
          {mostRecentFriday ? (
            <button
              onClick={() => setDateOverride(mostRecentFriday.value)}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-5 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-colors"
              data-testid="worksheet-show-friday"
            >
              <FileText className="w-4 h-4" /> Show {mostRecentFriday.label}'s Work
            </button>
          ) : (
            <div className="text-[11px] text-zinc-500 italic">No prior school days available yet — see you Monday.</div>
          )}
        </div>
      )}

      {open && ws?.is_preparing && (
        <div className="bg-black/50 border-t-2 border-orange-500/40 px-5 py-8 text-center" data-testid="worksheet-preparing">
          <div className="text-[10px] font-black tracking-[0.3em] uppercase text-orange-400 mb-2">Almost ready</div>
          <div className="text-white text-lg font-black mb-1">{ws.title}</div>
          <p className="text-xs text-zinc-400 max-w-md mx-auto">{ws.instructions}</p>
        </div>
      )}

      {open && !ws?.is_weekend && !ws?.is_preparing && (
        /* paper worksheet */
        <div className="bg-white text-zinc-900 p-6 sm:p-8" data-testid="worksheet-paper">
          {/* Worksheet header */}
          <div className="border-b-2 border-zinc-900 pb-3 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[10px] font-black tracking-[0.3em] uppercase text-orange-600">PRSVR Scholar Athlete Academy</div>
              <div className="flex gap-2">
                <button onClick={downloadPdf} disabled={downloading} className="flex items-center gap-1.5 border-2 border-zinc-900 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-zinc-900 hover:text-white transition-colors" data-testid="worksheet-download">
                  {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                </button>
              </div>
            </div>
            <h2 className="text-xl font-black mt-1">{ws.title}</h2>
            <div className="text-[11px] text-zinc-600 mt-1 flex flex-wrap gap-x-4">
              <span>Grade {ws.grade}</span><span>{ws.subject}</span><span>Standard {ws.standard_code}</span><span>{ws.date}</span>
            </div>
            {ws.instructions && <p className="text-[12px] text-zinc-700 mt-2"><span className="font-bold">Directions:</span> {ws.instructions}</p>}
          </div>

          {/* Today's Mini-Lesson — what the questions refer to */}
          {ws.mini_lesson && (
            <div className="mb-5 border-2 border-indigo-500 bg-indigo-50 p-4" data-testid="worksheet-mini-lesson">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-indigo-700" />
                <span className="text-[11px] font-black uppercase tracking-[0.2em] text-indigo-700">Today's Mini-Lesson · read this first</span>
              </div>
              <p className="text-[13px] text-zinc-800 leading-relaxed">{ws.mini_lesson}</p>
              <p className="text-[10px] text-indigo-700/70 mt-2 italic">Use what you learned here to answer the questions below.</p>
            </div>
          )}
          {ws.course_override === 'geometry' && <RimClockDiagram photoUrl={RIM_CLOCK_PHOTO} />}

          {/* Part A — Multiple Choice */}
          {mc.length > 0 && (
            <div className="mb-6">
              <div className="text-[11px] font-black tracking-widest uppercase text-orange-600 mb-3">Part A · Multiple Choice</div>
              {mc.map((q) => (
                <div key={q.id} className="mb-4" data-testid={`ws-q-${q.id}`}>
                  <p className="text-sm font-medium"><span className="font-black">{num[q.id]}.</span> {q.prompt}</p>
                  <div className="mt-2 space-y-1.5 pl-5">
                    {(q.options || []).map((opt, i) => {
                      const val = LETTERS[i];
                      const sel = (answers[q.id] || '').toUpperCase() === val;
                      return (
                        <label key={i} className={`flex items-center gap-2.5 text-[13px] cursor-pointer ${result ? 'pointer-events-none' : ''}`}>
                          <input type="radio" name={q.id} disabled={!!result} checked={sel} onChange={() => setAns(q.id, val)} className="accent-orange-600 w-4 h-4" data-testid={`ws-${q.id}-opt-${val}`} />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                  <QResult q={q} />
                </div>
              ))}
            </div>
          )}

          {/* Part B — Fill in the blank */}
          {fb.length > 0 && (
            <div className="mb-6">
              <div className="text-[11px] font-black tracking-widest uppercase text-orange-600 mb-3">Part B · Fill In The Blank</div>
              {ws.word_bank?.length > 0 && (
                <div className="mb-4 border-2 border-dashed border-orange-400 bg-orange-50 px-4 py-3" data-testid="ws-word-bank">
                  <div className="text-[10px] font-black tracking-[0.25em] uppercase text-orange-700 mb-2">Word Bank · Spelling Counts</div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                    {ws.word_bank.map((w) => <span key={w} className="text-sm font-bold text-zinc-800" data-testid={`ws-word-bank-${w}`}>{w}</span>)}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-2">Use a word from the bank for each blank. Not every word is used. Study these for Friday's spelling test.</p>
                </div>
              )}
              {fb.map((q) => (
                <div key={q.id} className="mb-4" data-testid={`ws-q-${q.id}`}>
                  <p className="text-sm font-medium"><span className="font-black">{num[q.id]}.</span> {q.prompt}</p>
                  <input value={answers[q.id] || ''} disabled={!!result} onChange={(e) => setAns(q.id, e.target.value)} placeholder="Your answer" className="mt-2 ml-5 w-64 max-w-full border-b-2 border-zinc-400 focus:border-orange-600 outline-none text-sm py-1 bg-transparent disabled:opacity-70" data-testid={`ws-${q.id}-input`} />
                  <QResult q={q} />
                </div>
              ))}
            </div>
          )}

          {/* Part C — Problem solving */}
          {pr.length > 0 && (
            <div className="mb-2">
              <div className="text-[11px] font-black tracking-widest uppercase text-orange-600 mb-3">Part C · Problem Solving</div>
              {pr.map((q) => (
                <div key={q.id} className="mb-4" data-testid={`ws-q-${q.id}`}>
                  <p className="text-sm font-medium"><span className="font-black">{num[q.id]}.</span> {q.prompt}</p>
                  <textarea value={answers[q.id] || ''} disabled={!!result} onChange={(e) => setAns(q.id, e.target.value)} rows={4} placeholder="Show your work and explain your thinking…" className="mt-2 w-full border-2 border-zinc-300 focus:border-orange-600 outline-none text-sm p-2 rounded-sm disabled:opacity-70" data-testid={`ws-${q.id}-input`} />
                  <QResult q={q} />
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center gap-3 flex-wrap">
            {!result ? (
              <button onClick={submit} disabled={submitting} className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-400 text-white px-5 py-2.5 font-black text-xs tracking-[0.2em] uppercase transition-colors" data-testid="worksheet-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Grading…</> : <><Sparkles className="w-4 h-4" /> Submit &amp; Check</>}
              </button>
            ) : result.locked ? (
              <div className="flex items-center gap-2 border-2 border-emerald-600 bg-emerald-50 text-emerald-800 px-4 py-2 font-black text-xs tracking-[0.2em] uppercase" data-testid="worksheet-locked">
                <CheckCircle2 className="w-4 h-4" /> Submitted · Locked
              </div>
            ) : (
              <button onClick={retry} className="flex items-center gap-2 border-2 border-zinc-900 px-5 py-2.5 font-black text-xs tracking-[0.2em] uppercase hover:bg-zinc-900 hover:text-white transition-colors" data-testid="worksheet-retry">
                <RefreshCw className="w-4 h-4" /> Try Again
              </button>
            )}
            {result && <span className="text-xs text-zinc-600 font-bold">Objective score: {result.objective_correct}/{result.objective_total} ({result.objective_score}%)</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyWorksheet;
